import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/common/Combobox';
import { AlertCircle, Sparkles } from 'lucide-react';
import { settlementService } from '@/services/settlementService';
import { SettlementAdjustmentRecord, RateRuleRecord } from '@/types/database';
import { roundToCentavos } from '@/engine/settlement_calculator';

interface SettlementAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cycleId: string;
  settlementId: string;
  farmId?: string | null;
  growerId?: string | null;
  adjustmentToEdit?: SettlementAdjustmentRecord | null;
}

const ACCOUNT_OPTIONS = [
  { code: '4010', name: 'Retail Sales Raiser', defaultType: 'retail_sales', isDeduction: true },
  { code: '4020', name: 'Used Sacks', defaultType: 'used_sacks', isDeduction: false },
  { code: '4030', name: 'Cash Advance', defaultType: 'cash_advance', isDeduction: true },
  { code: '4031', name: 'Cash Advance - Feeds', defaultType: 'cash_advance_feeds', isDeduction: true },
  { code: '4040', name: 'Biogas', defaultType: 'biogas', isDeduction: true },
  { code: '4041', name: 'Biogas Maintenance', defaultType: 'biogas_maintenance', isDeduction: true },
  { code: '4050', name: "Raiser's Expense", defaultType: 'raiser_expense', isDeduction: false },
];

export const SettlementAdjustmentModal: React.FC<SettlementAdjustmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  cycleId,
  settlementId,
  farmId,
  growerId,
  adjustmentToEdit,
}) => {
  const [accountCode, setAccountCode] = useState<string>('4020');
  const [adjustmentDate, setAdjustmentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [unitRate, setUnitRate] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [rateRule, setRateRule] = useState<RateRuleRecord | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize or populate form
  useEffect(() => {
    if (adjustmentToEdit) {
      setAccountCode(adjustmentToEdit.account_code);
      setAdjustmentDate(adjustmentToEdit.adjustment_date);
      setDescription(adjustmentToEdit.description);
      setQuantity(adjustmentToEdit.quantity ? adjustmentToEdit.quantity.toString() : '');
      setUnitRate(adjustmentToEdit.unit_rate ? adjustmentToEdit.unit_rate.toString() : '');
      setAmount(adjustmentToEdit.amount.toString());
      setNotes(adjustmentToEdit.notes || '');
      setRateRule(adjustmentToEdit.rate_rules || null);
    } else {
      setAccountCode('4020');
      setAdjustmentDate(new Date().toISOString().split('T')[0]);
      setDescription('Used Sacks Salvage');
      setQuantity('');
      setUnitRate('');
      setAmount('');
      setNotes('');
      setRateRule(null);
      lookupRateRule('4020', new Date().toISOString().split('T')[0]);
    }
  }, [adjustmentToEdit, isOpen]);

  // Query rate rule suggestion dynamically
  const lookupRateRule = async (code: string, date: string) => {
    if (code === '4020' || code === '4040' || code === '4041') {
      const suggested = await settlementService.getRateRuleSuggestion(code, date, farmId, growerId);
      if (suggested) {
        setRateRule(suggested);
        if (!adjustmentToEdit) {
          setUnitRate(suggested.rate_value.toString());
          if (quantity) {
            const qtyNum = parseFloat(quantity);
            if (!isNaN(qtyNum)) {
              setAmount(roundToCentavos(qtyNum * suggested.rate_value).toFixed(2));
            }
          }
        }
      } else {
        setRateRule(null);
      }
    } else {
      setRateRule(null);
    }
  };

  const handleAccountChange = (newCode: string) => {
    setAccountCode(newCode);
    const opt = ACCOUNT_OPTIONS.find((o) => o.code === newCode);
    if (opt && !adjustmentToEdit) {
      setDescription(opt.name);
    }
    lookupRateRule(newCode, adjustmentDate);
  };

  const handleQuantityChange = (val: string) => {
    setQuantity(val);
    const qtyNum = parseFloat(val);
    const rateNum = parseFloat(unitRate);
    if (!isNaN(qtyNum) && !isNaN(rateNum)) {
      setAmount(roundToCentavos(qtyNum * rateNum).toFixed(2));
    }
  };

  const handleUnitRateChange = (val: string) => {
    setUnitRate(val);
    const qtyNum = parseFloat(quantity);
    const rateNum = parseFloat(val);
    if (!isNaN(qtyNum) && !isNaN(rateNum)) {
      setAmount(roundToCentavos(qtyNum * rateNum).toFixed(2));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('Please enter a valid positive adjustment amount.');
      return;
    }

    if (!description.trim()) {
      setError('Please enter a description for the adjustment.');
      return;
    }

    setLoading(true);
    try {
      const selectedOption = ACCOUNT_OPTIONS.find((o) => o.code === accountCode);
      const adjustmentType = selectedOption?.defaultType || 'other';

      if (adjustmentToEdit) {
        const res = await settlementService.updateSettlementAdjustment(adjustmentToEdit.id, {
          amount: parsedAmount,
          quantity: quantity ? parseFloat(quantity) : null,
          unit_rate: unitRate ? parseFloat(unitRate) : null,
          description: description.trim(),
          adjustment_date: adjustmentDate,
          notes: notes.trim() || null,
        });
        if (!res.success) throw new Error(res.error || 'Failed to update adjustment');
      } else {
        const res = await settlementService.createSettlementAdjustment({
          settlement_id: settlementId,
          cycle_id: cycleId,
          account_code: accountCode,
          adjustment_type: adjustmentType,
          adjustment_date: adjustmentDate,
          amount: parsedAmount,
          quantity: quantity ? parseFloat(quantity) : null,
          unit_rate: unitRate ? parseFloat(unitRate) : null,
          rate_rule_id: rateRule?.id || null,
          description: description.trim(),
          notes: notes.trim() || null,
        });
        if (!res.success) throw new Error(res.error || 'Failed to create adjustment');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving the adjustment.');
    } finally {
      setLoading(false);
    }
  };

  const accountComboboxOptions = ACCOUNT_OPTIONS.map((opt) => ({
    value: opt.code,
    label: `[${opt.code}] ${opt.name} (${opt.isDeduction ? 'Deduction' : 'Credit / Addition'})`,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader className="pb-3 border-b border-slate-200">
          <DialogTitle className="text-base font-bold text-slate-900">
            {adjustmentToEdit ? 'Edit Grower Adjustment' : 'Add Grower Adjustment'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-md flex items-start gap-2 text-xs text-rose-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Account Category Selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Adjustment Account (Chart of Accounts)
            </label>
            {adjustmentToEdit ? (
              <Input
                type="text"
                value={`[${accountCode}] ${ACCOUNT_OPTIONS.find((o) => o.code === accountCode)?.name || accountCode}`}
                disabled
                className="h-10 text-xs bg-slate-50 font-medium"
              />
            ) : (
              <Combobox
                options={accountComboboxOptions}
                value={accountCode}
                onChange={handleAccountChange}
                placeholder="Select Adjustment Account..."
                searchPlaceholder="Search account..."
              />
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Adjustment Date</label>
            <Input
              type="date"
              value={adjustmentDate}
              onChange={(e) => {
                setAdjustmentDate(e.target.value);
                lookupRateRule(accountCode, e.target.value);
              }}
              required
              className="h-10 text-xs font-mono"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Description / Particulars</label>
            <Input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Used Sacks (1,040 bags), Cash Advance for laborers"
              required
              className="h-10 text-xs"
            />
          </div>

          {/* Dynamic Rate Rule Suggestion Badge */}
          {rateRule && (
            <div className="p-2.5 bg-indigo-50/70 border border-indigo-100 rounded-lg flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 text-indigo-900">
                <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                <span className="font-semibold">Suggested Rate Rule:</span>
                <span>{rateRule.rule_name}</span>
              </div>
              <span className="font-mono font-bold text-indigo-700">
                ₱{rateRule.rate_value.toFixed(2)} / {rateRule.rate_unit}
              </span>
            </div>
          )}

          {/* Quantity & Unit Rate (Optional / Dynamic) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Quantity (Units / Sacks)</label>
              <Input
                type="number"
                step="any"
                inputMode="decimal"
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                placeholder="Optional (e.g. 1040)"
                className="h-10 text-xs font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Unit Rate (₱)</label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={unitRate}
                onChange={(e) => handleUnitRateChange(e.target.value)}
                placeholder="Optional (e.g. 2.50)"
                className="h-10 text-xs font-mono"
              />
            </div>
          </div>

          {/* Total Amount */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Total Adjustment Amount (₱) <span className="text-rose-500">*</span>
            </label>
            <Input
              type="number"
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="h-10 text-sm font-bold font-mono text-indigo-950"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Internal Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes or receipt references..."
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-xs text-slate-800 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
          </div>

          <DialogFooter className="sticky bottom-0 bg-white pt-3 border-t border-slate-200 flex items-center justify-end gap-2 z-10">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading} className="min-h-[44px]">
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="min-h-[44px] bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-4"
            >
              {loading ? 'Saving...' : adjustmentToEdit ? 'Save Changes' : 'Add Adjustment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
